import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { PanelRight, X } from 'lucide-react';
import * as React from 'react';

import { FieldGrid, FieldGridItem, NewTabLink, ParticipantAvatar, useEscapeClose, useFitHeight } from '@/design';
import type { IListFieldCatalogue, IListField, IListRow } from '../../api/lists-contracts';
import { NotesLog } from '../../components/notes-log';
import { recordSurveyPath } from '../../components/participant-link';
import { catalogueSections, listSurveyLabel } from '../../lists';
import { isNotesLog } from '../../notes-log';
import {
  CellValue,
  EditableCell,
  LONG_TEXT_WHOLE,
  SourceMark,
  cellText,
  editedText,
  type ICellEdit
} from './cells';
import { nameOf } from './editing';

/** The pane's two modes: the row read, or its editable cells as controls. */
export type PaneMode = 'view' | 'edit';

/**
 * *Reading pane* in the toolbar: pressed while a pane is drawn, said by its look as well as by
 * `aria-pressed` — the pane's own View · Edit pressed tone, its teal edge drawn twice so the press
 * reads apart from the outline button's hover tint. A ghost button's pressed state looked like its
 * released one, and it read pressed with no pane drawn at all. A press with none drawn opens one
 * (the caller's: the row last read, else the page's first).
 */
export function ReadingPaneToggle({
  shown,
  disabled,
  onPress
}: {
  shown: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-pressed={shown}
      disabled={disabled}
      onClick={onPress}
      className={cn(
        'h-8 gap-1.5 font-normal',
        shown &&
          'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] text-[var(--color-royal-blue)] shadow-[inset_0_0_0_1px_var(--color-teal)] hover:bg-[color-mix(in_srgb,var(--color-teal)_16%,white)]'
      )}
    >
      <PanelRight className="size-4" aria-hidden />
      Reading pane
    </Button>
  );
}

/**
 * The reading pane (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.9; the owner's Q5): **the row as a record** — the view's columns as label-over-value pairs, in
 * the catalogue's groups, under the person's name — and one link, **Open the record page**, to the
 * row's survey on the record page, in a new tab (SR-491). **Edit** draws the editable cells as controls, each staged exactly as a cell's — one
 * draft per cell, two places to make it.
 *
 * **Its width follows the screen's** — `min(34vw, 40rem)` beside the grid — and its fields the
 * pane's own (`FieldGrid`'s `framed`): one to a line until its content is 34rem wide (a pane some
 * 36rem wide), so a control takes the pane's width rather than half of a fixed 26rem.
 *
 * The head is initials, never a photo: the row carries whether a photo exists (`sys.photo`), not
 * its address. The grid stays the page's one `main`, so this is the screen's own region beside it
 * and not `SplitView`; **Esc closes it by the split view's rule** (`escapeIsFree`) — never out of a
 * field, never while a menu, listbox, popover or dialog is open.
 */
export function ReadingPane({
  row,
  columns,
  catalogue,
  mode,
  onMode,
  onClose,
  editOf,
  editLocked
}: {
  row: IListRow;
  /** The view's columns shown — what the record draws. */
  columns: readonly IListField[];
  catalogue: IListFieldCatalogue;
  mode: PaneMode;
  onMode: (mode: PaneMode) => void;
  onClose: () => void;
  /** A cell's edit, or null where it is read-only to this reader. */
  editOf: (row: IListRow, field: IListField) => Nullable<ICellEdit>;
  /** Why *Edit* is not offered — `NotPermitted`'s id, the reason drawn once on the screen. */
  editLocked: Nullable<string>;
}) {
  const titleId = React.useId();
  // Beside the grid it ends where the window does, as the grid's frame does (SR-439): its
  // `calc(100dvh - 5rem)` reached past the window's bottom from below the filters.
  const section = React.useRef<HTMLElement>(null);
  const fitted = useFitHeight(section);
  const name = nameOf(row);
  const shown = new Set(columns.map((field) => field.code));
  const sections = catalogueSections(catalogue, (field) => shown.has(field.code), shown);

  useEscapeClose(onClose);

  return (
    <section
      aria-labelledby={titleId}
      data-pane={mode}
      data-participant={row.participantId}
      // Under the grid on a narrower screen, reached by scrolling: not what the grid fits above.
      data-fit-skip=""
      ref={section}
      style={fitted === null ? undefined : ({ '--pane-fit': `${fitted}px` } as React.CSSProperties)}
      className="@container grid content-start gap-4 rounded-md border border-border bg-card p-4 min-[90rem]:sticky min-[90rem]:top-16 min-[90rem]:max-h-[var(--pane-fit,calc(100dvh-5rem))] min-[90rem]:overflow-y-auto"
    >
      <div className="flex items-start gap-3">
        <ParticipantAvatar photoUri={null} displayName={name} size="md" decorative />
        <div className="grid min-w-0 flex-1 gap-0.5">
          <h2 id={titleId} className="truncate text-base font-medium text-[var(--color-royal-blue)]">
            {name}
          </h2>
          <span className="text-xs text-muted-foreground">{listSurveyLabel(row.survey)}</span>
          <NewTabLink
            to={recordSurveyPath(row.participantId, row.survey)}
            label="Open the record page in a new tab"
            className="justify-self-start px-0 text-sm underline-offset-4"
          >
            Open the record page
          </NewTabLink>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="Close the reading pane"
          onClick={onClose}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>

      <div role="group" aria-label="Pane mode" className="flex gap-1 text-[0.8125rem]">
        {(['view', 'edit'] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            aria-pressed={mode === choice}
            aria-disabled={choice === 'edit' && editLocked !== null ? true : undefined}
            aria-describedby={choice === 'edit' && editLocked !== null ? editLocked : undefined}
            onClick={() => {
              if (choice === 'edit' && editLocked !== null) return;
              onMode(choice);
            }}
            className={cn(
              'h-7 rounded-full border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === choice
                ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] font-medium text-[var(--color-royal-blue)]'
                : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]',
              choice === 'edit' && editLocked !== null && 'cursor-not-allowed opacity-60'
            )}
          >
            {choice === 'view' ? 'View' : 'Edit'}
          </button>
        ))}
      </div>

      {sections.length === 0 && (
        <p className="text-sm text-muted-foreground">This view shows no columns to read here.</p>
      )}
      {sections.map((section) => (
        <div key={section.key} className="grid gap-2">
          <h3 className="text-xs font-medium tracking-wide text-[var(--veritant-royal-70)] uppercase">
            {section.title}
          </h3>
          <FieldGrid framed>
            {section.fields.map((field) => {
              const edit = editOf(row, field);
              const cell = row.cells[field.code];
              // A notes log as its entries, their initials named on hover (SR-494).
              const log =
                field.kind === 'long_text' && isNotesLog(field.code) && typeof cell?.v === 'string'
                  ? cell.v
                  : null;
              const label = field.number ? `${field.number} ${field.label}` : field.label;
              return (
                <FieldGridItem
                  key={field.code}
                  label={label}
                  filled={
                    (mode === 'edit' && edit !== null) ||
                    edit?.draft !== undefined ||
                    cellText(field, row.cells[field.code]) !== ''
                  }
                >
                  {mode === 'edit' && edit ? (
                    <EditableCell field={field} row={row} name={name} edit={edit} form />
                  ) : edit?.draft ? (
                    <span
                      data-cell={field.code}
                      data-pending=""
                      className={cn(
                        'block pl-1 shadow-[inset_3px_0_0_var(--color-yellow)]',
                        field.kind === 'long_text' && LONG_TEXT_WHOLE
                      )}
                    >
                      {editedText(field, row.cells[field.code], edit)}
                    </span>
                  ) : log !== null ? (
                    <span data-cell={field.code} className="block">
                      <NotesLog notes={log} startOpen />
                      {cell?.src === 'salesforce' && <SourceMark />}
                    </span>
                  ) : (
                    <CellValue field={field} cell={row.cells[field.code]} look={null} whole />
                  )}
                </FieldGridItem>
              );
            })}
          </FieldGrid>
        </div>
      ))}
    </section>
  );
}
